/**
 * Upcoming Events Display
 * Extracts and displays the next upcoming event from the MFN calendar widget
 */
export function initUpcomingEvents() {
    const containers = document.querySelectorAll('[data-upcoming-events]');
    if (!containers.length) return;

    containers.forEach(container => {
        // Wait for calendar widget to load, then extract upcoming event
        waitForCalendarAndExtractEvent(container);
    });
}

/**
 * Wait for the calendar widget to load and extract the upcoming event
 * @param {HTMLElement} container - The container element to update with the event
 */
function waitForCalendarAndExtractEvent(container) {
    // Look for either the main calendar table or the hidden one for upcoming events
    const table = document.querySelector('#mf_calendar_table, #calendar-upcoming #mf_calendar_table');
    
    if (!table) {
        // Calendar not loaded yet, check again
        setTimeout(() => waitForCalendarAndExtractEvent(container), 100);
        return;
    }

    const rows = table.querySelectorAll('tbody tr.mf_table-element');
    if (rows.length === 0) {
        // Calendar rows not loaded yet, check again
        setTimeout(() => waitForCalendarAndExtractEvent(container), 100);
        return;
    }

    // Calendar is loaded, extract the upcoming event
    extractUpcomingEvent(container, rows);
}

/**
 * Extract the upcoming event from calendar rows
 * @param {HTMLElement} container - The container to update
 * @param {NodeList} rows - The calendar table rows
 */
function extractUpcomingEvent(container, rows) {
    const now = new Date();
    let upcomingEvent = null;

    // Find the first event that is today or in the future
    for (const row of rows) {
        const dateCell = row.querySelector('.event_date');
        if (!dateCell) continue;

        const dateStr = dateCell.textContent.trim();
        const eventDate = new Date(dateStr);

        // Reset time to start of day for comparison
        const eventDateOnly = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate());
        const nowDateOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        if (eventDateOnly >= nowDateOnly) {
            const contentCell = row.querySelector('td:nth-child(2)');
            if (contentCell) {
                const titleEl = contentCell.querySelector('.event_title');
                const title = titleEl ? titleEl.textContent.trim() : '';

                upcomingEvent = {
                    date: eventDate,
                    title: title
                };
                break;
            }
        }
    }

    // Update the container with the event
    if (upcomingEvent) {
        displayUpcomingEvent(container, upcomingEvent);
    }
    // If no upcoming event, keep the default content
}

/**
 * Display the upcoming event in the container
 * @param {HTMLElement} container - The container element
 * @param {Object} event - The event data
 */
function displayUpcomingEvent(container, event) {
    // Get language from data attribute
    const lang = container.dataset.lang || 'en';

    // Format date
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    const dateStr = event.date.toLocaleDateString(lang, options);

    // Fade out and slide up current content
    container.style.opacity = '0';
    container.style.transform = 'translateY(-10px)';
    container.style.transition = 'opacity 300ms ease-in-out, transform 300ms ease-in-out';

    // Wait for fade out to complete, then update content and fade in from below
    setTimeout(() => {
        // Clear container and add event
        container.innerHTML = '';

        const eventElement = document.createElement('div');
        eventElement.innerHTML = `
            <p class="font-bold mb-2">${escapeHtml(event.title)}</p>
            <p class="text-white/80">${escapeHtml(dateStr)}</p>
        `;

        container.appendChild(eventElement);
        
        // Position new content below and fade in with slide up
        container.style.transform = 'translateY(10px)';
        container.style.opacity = '0';
        
        // Trigger reflow to ensure the transform is applied before animating back
        container.offsetHeight;
        
        container.style.transform = 'translateY(0)';
        container.style.opacity = '1';
    }, 300);
}

/**
 * Escape HTML to prevent XSS
 * @param {string} text - The text to escape
 * @returns {string} The escaped text
 */
function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}


